import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import { logEvent } from './logger';
import { getLargeVkPhoto, fetchVkPhotoMaxOrig } from '../utils/vkAvatar';

const VKAuth = ({ onLoginSuccess, onLoginError, redirectTo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getRedirectUrl = () => {
    if (redirectTo) return redirectTo;
    const params = new URLSearchParams(location.search);
    return params.get('redirect') || '/home';
  };

  const handleLoginSuccess = async (payload) => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!payload?.code || !payload?.device_id) {
        throw new Error('Неверные данные авторизации');
      }

      // Получаем токен VK
      const tokenData = await window.VKIDSDK.Auth.exchangeCode(payload.code, payload.device_id);
      if (!tokenData?.user_id) throw new Error('Не удалось получить ID пользователя');
      const { user_id: userId, access_token: vkAccessToken } = tokenData;

      // Получаем данные пользователя через SDK
      const userInfoResponse = await window.VKIDSDK.Auth.userInfo(tokenData.access_token);
      const userInfo = userInfoResponse?.user || userInfoResponse;
      const firstName = userInfo?.first_name || '';
      const lastName = userInfo?.last_name || '';
      const photoMaxOrig = await fetchVkPhotoMaxOrig(vkAccessToken, userId);
      const photo = await getLargeVkPhoto(
        photoMaxOrig || userInfo?.avatar || userInfo?.photo_200 || userInfo?.photo || ''
      );

      // Получаем список друзей через JSONP
      let activeFriends = [];
      let friendsFetched = false;
      let friendsLog = '';
      try {
        activeFriends = await new Promise((resolve, reject) => {
          const callbackName = 'vk_friends_cb_' + Date.now();
          const script = document.createElement('script');
          script.src = `https://api.vk.com/method/friends.get?access_token=${vkAccessToken}&user_id=${userId}&fields=photo_200,first_name,last_name,deactivated&v=5.199&callback=window.${callbackName}`;
          
          window[callbackName] = (response) => {
            delete window[callbackName];
            script.remove();
            if (response?.response?.items) {
              friendsFetched = true;
              resolve(response.response.items.filter(f => !f.deactivated));
            } else if (response?.error) {
              console.error('VK friends error:', response.error);
              resolve([]);
            } else {
              resolve([]);
            }
          };
          
          script.onerror = () => {
            delete window[callbackName];
            script.remove();
            console.error('JSONP failed');
            resolve([]);
          };
          
          document.head.appendChild(script);
        });
      } catch (friendsErr) {
        console.warn('Не удалось получить друзей:', friendsErr);
      }

      // Не переносим скрытых друзей обратно в friends_info при входе
      let visibleFriends = activeFriends;
      if (friendsFetched) {
        try {
          const { data: existingUser } = await supabase
            .from('new_users')
            .select('ban_info')
            .eq('vk_id', userId)
            .single();

          const bannedItems = (() => {
            try {
              const parsed = typeof existingUser?.ban_info === 'string'
                ? JSON.parse(existingUser.ban_info)
                : existingUser?.ban_info;
              return Array.isArray(parsed?.items) ? parsed.items : [];
            } catch {
              return [];
            }
          })();

          const bannedIds = new Set(bannedItems.map(item => String(item?.id)));
          visibleFriends = activeFriends.filter(friend => !bannedIds.has(String(friend?.id)));
        } catch (error) {
          console.warn('Не удалось проверить скрытых друзей при входе:', error);
        }
      }

      // Сохраняем лог для отладки
      localStorage.setItem('vkAuthDebug', JSON.stringify({
        userId,
        friendsLog,
        friendsCount: visibleFriends.length,
        timestamp: new Date().toISOString()
      }));

      // Авторизация в Supabase
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: `${userId}@vk.com`,
        password: process.env.REACT_APP_DEFAULT_PASSWORD,
      });

      const profilePayload = {
        first_name: firstName,
        last_name: lastName,
        photo_url: photo
      };

      if (friendsFetched) {
        profilePayload.friends_count = visibleFriends.length;
        profilePayload.friends_ids = visibleFriends.map(f => f.id);
        profilePayload.friends_info = {
          count: visibleFriends.length,
          items: visibleFriends
        };
      }

      if (authError) {
        // Если пользователь не существует - регистрируем
        if (authError.message === 'Invalid login credentials') {
          const { error: signUpError } = await supabase.auth.signUp({
            email: `${userId}@vk.com`,
            password: process.env.REACT_APP_DEFAULT_PASSWORD,
          });

          if (signUpError) throw signUpError;

          // Сохраняем профиль пользователя
          const { error: profileError } = await supabase
            .from('new_users')
            .insert([{
              vk_id: userId,
              email: `${userId}@vk.com`,
              ...profilePayload
            }]);

          if (profileError) throw profileError;

          // Логируем создание нового пользователя
          await logEvent(
            'new_user_created',
            userId.toString(),
            null,
            `Новый пользователь: ${firstName} ${lastName}`,
            true
          );

          const redirectUrl = getRedirectUrl();
          onLoginSuccess?.();
          navigate(redirectUrl, { replace: true });
          return;
        }
        throw authError;
      }

      // Обновляем имя, фото и друзей
      const { error: updateError } = await supabase
        .from('new_users')
        .update(profilePayload)
        .eq('vk_id', userId);
      if (updateError) console.error('Update error:', updateError);

      // Логируем вход пользователя
      await logEvent(
        'user_login',
        userId.toString(),
        null,
        `Вход: ${firstName} ${lastName}`,
        false
      );

      onLoginSuccess?.();
      navigate(getRedirectUrl(), { replace: true });
    } catch (error) {
      console.error('Auth error:', error);
      onLoginError?.(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    const initVKAuth = () => {
      const container = document.getElementById('vkid-container');
      if (!container || !window.VKIDSDK) {
        setError('VKIDSDK не загружен. Попробуйте обновить страницу или используйте другой браузер.');
        return;
      }


      try {
        if (!window.VKIDSDK.Config || !window.VKIDSDK.OneTap) {
          throw new Error('VKIDSDK неполный. Попробуйте обновить страницу.');
        }
        
        window.VKIDSDK.Config.init({
          app: 53128580,
          redirectUrl: window.location.origin,
          responseMode: window.VKIDSDK.ConfigResponseMode.Callback,
          source: window.VKIDSDK.ConfigSource.LOWCODE,
          scope: 'friends',
        });


        new window.VKIDSDK.OneTap()
          .render({ 
            container, 
            showAlternativeLogin: true,
            style: {
              width: 300,
              height: 48,
            }
          })
          .on(window.VKIDSDK.WidgetEvents.ERROR, (error) => {
            console.error('VK Auth error:', error);
            const errorMsg = typeof error === 'object' ? JSON.stringify(error) : error;
            setError('Ошибка VKIDSDK: ' + errorMsg);
            onLoginError?.(error);
          })
          .on(window.VKIDSDK.OneTapInternalEvents.LOGIN_SUCCESS, handleLoginSuccess);
      } catch (e) {
        setError('Ошибка инициализации VKIDSDK: ' + (e?.message || e));
      }
    };

    const timer = setTimeout(() => {
      if (window.VKIDSDK) {
        initVKAuth();
      } else {
        setError('VKIDSDK не загружен. Попробуйте обновить страницу или используйте другой браузер.');
        console.warn('VKID SDK not loaded');
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="vkauth-container">
      <div id="vkid-container" />
      
      {isLoading && (
        <div className="vkauth-loading">
          <div className="vkauth-progress">
            <div className="vkauth-progress-bar" />
          </div>
          <p className="vkauth-loading-text">Вход в аккаунт...</p>
        </div>
      )}
      
      {error && (
        <div className="vkauth-error">{error}</div>
      )}
    </div>
  );
};

export default VKAuth;